import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parse from 'html-react-parser';
import { NumericFormat } from 'react-number-format';
import AlertBox from './AlertBox';
import type { BudgetLinesElementType, BudgetLinesRow } from '../helpers/types';

type Props = {
    value: BudgetLinesRow[];
    onChange: (rows: BudgetLinesRow[]) => void;
    error?: string | null;
};

const BudgetLines = (props: BudgetLinesElementType & Props) => {
    const { label, description, columns, addButtonText, value: rows, onChange, error } = props;

    const total = rows.reduce((sum, row) => {
        return sum + (Number(String(row.belop ?? '').replace(/\s+/g, '')) || 0);
    }, 0);

    const emptyRow = (): BudgetLinesRow => {
        const row: BudgetLinesRow = { belop: '' };
        columns.forEach((col) => { row[col.name] = ''; });
        return row;
    };

    const updateCell = (index: number, key: string, val: string | number) => {
        onChange(rows.map((row, i) => i === index ? { ...row, [key]: val } : row));
    };

    const removeRow = (index: number) => {
        onChange(rows.filter((_, i) => i !== index));
    };

    return (
        <div className="budget-lines">
            <label className="form-label">{label}</label>
            {description && <div className="text-muted small mb-2">{parse(description)}</div>}

            {rows.length > 0 && (
                <div className="d-flex align-items-center gap-2 pb-2 mb-1 border-bottom">
                    {columns.map((col) => (
                        <div key={col.name} className="flex-grow-1">
                            <small className="text-muted fw-semibold">{col.label}</small>
                        </div>
                    ))}
                    <div style={{ minWidth: 150 }} className="text-end">
                        <small className="text-muted fw-semibold">Beløp</small>
                    </div>
                    <div style={{ width: 36 }} />
                </div>
            )}

            {rows.map((row, index) => (
                <div key={index} className="d-flex align-items-center gap-2 mb-2">
                    {columns.map((col) => (
                        <input
                            key={col.name}
                            type="text"
                            className="form-control"
                            value={String(row[col.name] ?? '')}
                            onChange={(e) => updateCell(index, col.name, e.target.value)}
                            placeholder={col.label}
                        />
                    ))}
                    <div style={{ minWidth: 150 }}>
                        <NumericFormat
                            value={row.belop !== '' ? row.belop : ''}
                            onValueChange={(values) => updateCell(index, 'belop', values.value)}
                            thousandSeparator=" "
                            className="form-control text-end"
                            placeholder="0"
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-link text-danger p-1 d-flex align-items-center"
                        style={{ width: 36 }}
                        onClick={() => removeRow(index)}
                        title="Fjern"
                    >
                        <FontAwesomeIcon icon={['fal', 'trash-can']} />
                    </button>
                </div>
            ))}

            <div className="mt-1 mb-2">
                <button
                    type="button"
                    className="btn btn-link text-primary ps-0"
                    onClick={() => onChange([...rows, emptyRow()])}
                >
                    + {addButtonText ?? 'Legg til kostnad'}
                </button>
            </div>

            {rows.length > 0 && (
                <div className="d-flex justify-content-end mt-2 mb-1">
                    <div className="text-end">
                        <div className="text-muted small">{rows.length} poster</div>
                        <div className="fs-5 fw-bold">Kr. {total.toLocaleString('nb-NO')}</div>
                        <div className="text-muted small text-uppercase">Totalsum</div>
                    </div>
                </div>
            )}

            {error && (
                <AlertBox classes="mt-3">{error}</AlertBox>
            )}
        </div>
    );
};

export default BudgetLines;
